// NpcSectionHeader — frames a Personal-tab section as an in-world NPC host:
// an avatar chip (initials fallback) + the section title + a violet speech
// bubble line (WU-W1). Renders fully from the static host.npcName / host.bubble
// (there is NO NPC roster endpoint). When host.npcId is set it best-effort
// enriches the bubble with the NPC's card.greeting via GET /api/npcs/{id}/card
// — never blocks render, cancel-on-unmount. See WU-D1 §D-2 C1.

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { npcService } from '../../services/api';
import type { NpcCard } from '../../services/api';
import type { NpcSectionHost } from '../../data/npcSectionHosts';
import './NpcSectionHeader.css';

interface NpcSectionHeaderProps {
  host: NpcSectionHost;
  children?: ReactNode;
}

/** First-letter initials of an NPC name (e.g. "Sister Lyra" → "SL"). */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

export function NpcSectionHeader({ host, children }: NpcSectionHeaderProps) {
  const [card, setCard] = useState<NpcCard | null>(null);

  useEffect(() => {
    if (!host.npcId) return;
    let cancelled = false;
    npcService
      .getNpcCard(host.npcId)
      .then((c) => {
        if (!cancelled) setCard(c);
      })
      .catch(() => {
        /* best-effort enrichment only — bubble stays on the static line */
      });
    return () => {
      cancelled = true;
    };
  }, [host.npcId]);

  const bubble = card?.greeting || host.bubble;

  return (
    <div className="npc-section-header">
      <div className="npc-avatar-chip" aria-hidden="true">
        {initialsOf(host.npcName)}
      </div>
      <div className="npc-section-header-body">
        <span className="npc-host-name">{host.npcName}</span>
        <h3 className="npc-section-title">{host.title}</h3>
        <p className="npc-speech-bubble">{bubble}</p>
        {children}
      </div>
    </div>
  );
}

export default NpcSectionHeader;
